// Broadnet → Reglas de reverso: códigos de respuesta del switch y qué acción dispara
// (ok/reverso/reintento/confirmar/error).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  ACCIONES_CODIGO_BN, BnCodigoRespuesta, Campo, Modal, Switch,
  bnCodigoGuardar, bnCodigosListar, bnCodigosSeed, fieldCls,
} from './shared';

const ACCION_CLS: Record<string, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  reverso: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  reintento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  confirmar: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  error: 'bg-muted text-muted-foreground',
};

const vacio = { id: '', codigo: '', accion: 'reverso' as BnCodigoRespuesta['accion'], descripcion: '', es_activo: true };

export function CodigosBroadnet() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: codigos = [], isFetching } = useQuery<BnCodigoRespuesta[]>({
    queryKey: ['pos-bn-codigos'], queryFn: bnCodigosListar, staleTime: 0,
  });

  const seed = useMutation({
    mutationFn: () => bnCodigosSeed(),
    onSuccess: (r) => { toast.success(`Reglas importadas (${r.insertados} nuevas)`); void qc.invalidateQueries({ queryKey: ['pos-bn-codigos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: BnCodigoRespuesta) => {
    if (c) { setEditId(c.id); setForm({ id: c.id, codigo: c.codigo, accion: c.accion, descripcion: c.descripcion ?? '', es_activo: c.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => bnCodigoGuardar({
      id: editId || undefined, codigo: form.codigo.trim(), accion: form.accion,
      descripcion: form.descripcion.trim() || null, es_activo: form.es_activo,
    }),
    onSuccess: () => { toast.success('Regla guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-bn-codigos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleActivo = useMutation({
    mutationFn: (c: BnCodigoRespuesta) => bnCodigoGuardar({ id: c.id, codigo: c.codigo, accion: c.accion, descripcion: c.descripcion, es_activo: !c.es_activo }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pos-bn-codigos'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Undo2 className="w-5 h-5" /> Broadnet · Reglas de reverso</h1>
        <div className="flex gap-2">
          <Button variant="outline" isLoading={seed.isPending}
            onClick={async () => { if (await dlg.confirmar('¿Importar las reglas del manual?', { detalle: 'Son los códigos de respuesta del manual de Broadnet; no duplica lo existente.' })) seed.mutate(); }}>
            Importar reglas del manual
          </Button>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Códigos ({codigos.length})
          {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Acción</th>
              <th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Activo</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {codigos.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin reglas — usa &quot;Importar reglas del manual&quot; o &quot;+ Nuevo&quot;</td></tr>
              )}
              {codigos.map((c, i) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.codigo}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ACCION_CLS[c.accion] ?? ''}`}>{c.accion}</span></td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{c.descripcion || '—'}</td>
                  <td className="py-2 pr-3"><Switch checked={c.es_activo} onChange={() => toggleActivo.mutate(c)} label="" /></td>
                  <td className="py-2 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar regla' : 'Nueva regla'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código *"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Acción *">
                <select className={fieldCls} value={form.accion} onChange={(e) => set({ accion: e.target.value as BnCodigoRespuesta['accion'] })}>
                  {ACCIONES_CODIGO_BN.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} /></Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.codigo.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
